import { z } from 'zod';
import { fuzzyDateSchema } from './fuzzy-date.js';
import { AGE_COMPARISONS, APPARENT_AGES, AUDIT_ACTIONS, AUDIT_ENTITY_TYPES, CITABLE_ENTITIES, EVENT_TYPES, GAP_KINDS, LIST_VIEWS, MAX_DETECTED_FACES, MEDIA_KINDS, PARENT_SUBTYPES, PARTNER_SUBTYPES, PROPOSABLE_FIELDS, PROPOSABLE_FIELD_TYPES, PROPOSAL_KINDS, RELATION_ROLES, RELATIONSHIP_SUBTYPES, QUESTION_KINDS, QUESTION_STATUSES, RELATIONSHIP_KINDS, SEXES, SOURCE_KINDS, USER_ROLES, } from './constants.js';
export const uuid = z.string().uuid();
const optionalUuid = z.union([uuid, z.literal('')]).transform((v) => (v === '' ? null : v)).nullable();
const shortText = z.string().trim().max(200);
const longText = z.string().trim().max(20_000);
/**
 * Boolean aus einem Query-String. `z.coerce.boolean()` ist hier falsch: es
 * macht aus jedem nicht-leeren String `true`, also auch aus `"false"`.
 */
export const queryBoolean = z
    .enum(['true', 'false', '1', '0', ''])
    .transform((v) => v === 'true' || v === '1')
    .or(z.boolean());
// ── Auth ───────────────────────────────────────────────────────────────────
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Keine gültige E-Mail-Adresse'),
    password: z.string().min(1, 'Passwort fehlt'),
    remember: z.boolean().optional().default(true),
});
export const passwordSchema = z
    .string()
    .min(10, 'Mindestens 10 Zeichen')
    .max(200, 'Höchstens 200 Zeichen');
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
export const acceptInviteSchema = z.object({
    token: z.string().min(10),
    name: shortText.min(1, 'Name fehlt'),
    password: passwordSchema,
});
export const createInviteSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    role: z.enum(USER_ROLES).default('editor'),
    /** Wer das im Stammbaum ist – optional, aber hilfreich für Familienfragen. */
    personId: optionalUuid.optional(),
});
export const updateUserSchema = z.object({
    name: shortText.min(1).optional(),
    role: z.enum(USER_ROLES).optional(),
    isActive: z.boolean().optional(),
    personId: optionalUuid.optional(),
});
/**
 * Bezugspunkt für die Mitgliederliste: zu welcher Person bzw. welchem Bild
 * wird gefragt. Daraus ergibt sich, wer am nächsten dransteht.
 */
export const familyQuerySchema = z.object({
    personId: uuid.optional(),
    mediaId: uuid.optional(),
});
/**
 * Jedes Feld einzeln änderbar: das Profilformular schickt den Namen, der
 * Ansichtsumschalter der Personenliste nur seine eine Einstellung.
 */
export const updateProfileSchema = z.object({
    name: shortText.min(1, 'Name fehlt').optional(),
    locale: z.enum(['de', 'en']).optional(),
    theme: z.enum(['light', 'dark', 'system']).optional(),
    personsView: z.enum(LIST_VIEWS).optional(),
});
// ── Archiv ─────────────────────────────────────────────────────────────────
export const archiveInputSchema = z.object({
    name: shortText.min(1, 'Name fehlt').optional(),
    subtitle: shortText.nullable().optional(),
    subtitleAuto: z.boolean().optional(),
    // Der Schutz von Bild- und Personendaten. Die Grenzen sind dieselben wie
    // die Prüfbedingungen an der Tabelle – ein Wert, den die Datenbank ablehnt,
    // soll schon hier als Feldfehler im Formular landen und nicht als 500er.
    scopeEnforcement: z.boolean().optional(),
    scopeRadius: z.coerce.number().int().min(0).max(12).optional(),
    photoCutoffYears: z.coerce.number().int().min(0).max(200).optional(),
    veilMode: z.enum(['veil', 'hide']).optional(),
});
/** Leerer Text heißt: persönliche Bezeichnung entfernen. */
export const archiveLabelSchema = z.object({
    label: shortText.transform((value) => (value === '' ? null : value)).nullable(),
});
// ── Zweige & Freigaben ─────────────────────────────────────────────────────
export const branchInputSchema = z.object({
    name: shortText.min(1, 'Name fehlt'),
    apexPersonId: z.string().uuid(),
    direction: z.enum(['descendants', 'ancestors', 'both']).default('descendants'),
    maxDepth: z.coerce.number().int().min(1).max(20).nullable().default(null),
    includePartners: z.boolean().default(true),
    note: shortText.nullable().default(null),
});
export const branchPatchSchema = branchInputSchema.partial();
/** Die vollständige Liste der Konten – nicht ein Zusatz, sondern der Stand. */
export const branchGrantsSchema = z.object({
    userIds: z.array(z.string().uuid()).max(200),
});
export const accessRequestInputSchema = z
    .object({
    kind: z.enum(['branch', 'media']),
    apexPersonId: z.string().uuid().nullable().default(null),
    mediaId: z.string().uuid().nullable().default(null),
    message: z.string().trim().max(1000).nullable().default(null),
})
    .superRefine((value, ctx) => {
    if (value.kind === 'branch' && !value.apexPersonId) {
        ctx.addIssue({ code: 'custom', path: ['apexPersonId'], message: 'Person fehlt' });
    }
    if (value.kind === 'media' && !value.mediaId) {
        ctx.addIssue({ code: 'custom', path: ['mediaId'], message: 'Medium fehlt' });
    }
});
export const accessDecisionSchema = z.object({
    grant: z.boolean(),
    note: shortText.nullable().default(null),
    /** Bei einer Zweigfreigabe: welcher vorhandene Zweig – sonst wird einer angelegt. */
    branchId: z.string().uuid().nullable().default(null),
});
// ── Personen ───────────────────────────────────────────────────────────────
export const personInputSchema = z.object({
    givenNames: shortText.min(1, 'Vorname fehlt'),
    surname: shortText.default(''),
    birthSurname: shortText.nullable().default(null),
    nickname: shortText.nullable().default(null),
    sex: z.enum(SEXES).default('unknown'),
    isLiving: z.boolean().default(false),
    isUnverified: z.boolean().default(false),
    birth: fuzzyDateSchema.nullable().default(null),
    death: fuzzyDateSchema.nullable().default(null),
    birthPlaceId: optionalUuid.default(null),
    deathPlaceId: optionalUuid.default(null),
    occupation: shortText.nullable().default(null),
    notes: longText.nullable().default(null),
    avatarMediaId: optionalUuid.default(null),
});
export const personPatchSchema = personInputSchema.partial();
export const personListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    sex: z.enum(SEXES).optional(),
    living: z.enum(['yes', 'no']).optional(),
    /**
     * Nur Personen mit bzw. ohne Vorbehalt. Der Gegenwert zum Haken auf der
     * Personenseite: markieren hilft nur, wenn man die Markierten wiederfindet.
     */
    unverified: z.enum(['yes', 'no']).optional(),
    /**
     * Personen mit bzw. ohne verknüpftes Bild und mit bzw. ohne erfasste
     * Eltern.
     *
     * Beides sind die häufigsten Fragen an eine Personenliste in einem
     * Bildarchiv – „wer hat noch kein Gesicht“ und „wo hört die Linie auf“ –
     * und beides waren bisher nur Zähler auf der Übersicht, zu denen man die
     * Liste nicht bekommen konnte. Die Bedingungen sind wortgleich mit denen
     * in `services/gaps.ts`, damit Zahl und Liste zusammenpassen.
     */
    hasMedia: z.enum(['yes', 'no']).optional(),
    hasParents: z.enum(['yes', 'no']).optional(),
    /**
     * Personen mit bzw. *ohne jede* Verbindung – weder Eltern noch Kinder noch
     * Partner. Der Gegenpart zum Stammbaum: wer dort nicht gezeichnet wird,
     * muss auffindbar bleiben, sonst ist das Weglassen ein Verschwinden.
     */
    hasRelations: z.enum(['yes', 'no']).optional(),
    placeId: uuid.optional(),
    surname: shortText.optional(),
    bornAfter: z.coerce.number().int().min(0).max(3000).optional(),
    bornBefore: z.coerce.number().int().min(0).max(3000).optional(),
    sort: z
        .enum(['surname', 'givenNames', 'birth', 'death', 'updatedAt', 'createdAt'])
        .default('surname'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
// ── Beziehungen ────────────────────────────────────────────────────────────
export const relationshipInputSchema = z
    .object({
    kind: z.enum(RELATIONSHIP_KINDS),
    /** Bei kind=parent: der Elternteil. Bei kind=partner: Partner A. */
    personAId: uuid,
    /** Bei kind=parent: das Kind. Bei kind=partner: Partner B. */
    personBId: uuid,
    subtype: z.string().max(40).nullable().default(null),
    start: fuzzyDateSchema.nullable().default(null),
    end: fuzzyDateSchema.nullable().default(null),
    placeId: optionalUuid.default(null),
    notes: longText.nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.personAId === v.personBId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Person kann nicht mit sich selbst verknüpft werden',
            path: ['personBId'],
        });
    }
    const allowed = v.kind === 'parent' ? PARENT_SUBTYPES : PARTNER_SUBTYPES;
    if (v.subtype && !allowed.includes(v.subtype)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Art passt nicht zu „${v.kind}“`,
            path: ['subtype'],
        });
    }
});
// ── Orte ───────────────────────────────────────────────────────────────────
export const placeInputSchema = z.object({
    name: shortText.min(1, 'Name fehlt'),
    adminArea: shortText.nullable().default(null),
    country: shortText.nullable().default(null),
    lat: z.coerce.number().min(-90).max(90).nullable().default(null),
    lng: z.coerce.number().min(-180).max(180).nullable().default(null),
    notes: longText.nullable().default(null),
});
export const placeListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    country: shortText.optional(),
    sort: z.enum(['name', 'country', 'usage', 'createdAt']).default('name'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
// ── Ereignisse ─────────────────────────────────────────────────────────────
export const eventInputSchema = z
    .object({
    type: z.enum(EVENT_TYPES),
    title: shortText.nullable().default(null),
    date: fuzzyDateSchema.nullable().default(null),
    placeId: optionalUuid.default(null),
    description: longText.nullable().default(null),
    personIds: z.array(uuid).max(50).default([]),
    relationshipId: optionalUuid.default(null),
})
    .superRefine((v, ctx) => {
    if (v.personIds.length === 0 && !v.relationshipId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Mindestens eine beteiligte Person auswählen',
            path: ['personIds'],
        });
    }
});
export const eventListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    type: z.enum(EVENT_TYPES).optional(),
    personId: uuid.optional(),
    placeId: uuid.optional(),
    fromYear: z.coerce.number().int().min(0).max(3000).optional(),
    toYear: z.coerce.number().int().min(0).max(3000).optional(),
    sort: z.enum(['date', 'type', 'createdAt']).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
// ── Medien ─────────────────────────────────────────────────────────────────
export const mediaPatchSchema = z.object({
    title: shortText.nullable().optional(),
    description: longText.nullable().optional(),
    takenAt: fuzzyDateSchema.nullable().optional(),
    placeId: optionalUuid.optional(),
    personIds: z.array(uuid).max(100).optional(),
});
/**
 * Eine beantwortete Vergleichsfrage.
 *
 * Die Richtung steckt in den Feldnamen: `verdict` gilt für `regionId` gegen
 * `referenceRegionId`. Ohne diese Festlegung wäre jede gespeicherte Antwort
 * mehrdeutig, und mehrdeutige Beobachtungen sind schlimmer als keine.
 */
export const ageComparisonInputSchema = z.object({
    regionId: uuid,
    referenceRegionId: uuid,
    verdict: z.enum(AGE_COMPARISONS),
});
export const mediaListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    kind: z.enum(MEDIA_KINDS).optional(),
    personId: uuid.optional(),
    placeId: uuid.optional(),
    /** Nichts und niemand verknüpft. */
    untagged: z.enum(['yes']).optional(),
    /** Bilder ohne markierte Bildbereiche – die Arbeitsliste fürs Markieren. */
    unmarked: z.enum(['yes']).optional(),
    /** Bilder mit Gesichtern, die noch keinen Namen haben. */
    unknownFaces: z.enum(['yes']).optional(),
    /**
     * Jahrzehnt des Aufnahmedatums, z. B. `1950` für 1950–1959.
     *
     * Ein eigener Wert und keine zwei Jahresgrenzen: gestöbert wird in
     * Jahrzehnten, und ein Paar aus „von“ und „bis“ lässt sich in der Adresse
     * widersprüchlich füllen. Bilder ohne Datum fallen heraus – sie stehen unter
     * `withoutDate`.
     */
    decade: z.coerce.number().int().min(1000).max(3000).optional(),
    /** Bilder ohne Aufnahmedatum. */
    withoutDate: z.enum(['yes']).optional(),
    /**
     * `random` mischt die Treffer – für den Zufallsfund auf der Übersicht.
     *
     * Die Reihenfolge hängt an `seed` und ist damit wiederholbar: dieselbe
     * Adresse zeigt dasselbe Bild. Ohne das wäre jedes Nachladen ein anderer
     * Fund, und „schau dir das an“ ließe sich niemandem schicken.
     */
    sort: z.enum(['takenAt', 'createdAt', 'title', 'bytes', 'random']).default('createdAt'),
    seed: z.coerce.number().int().min(0).max(2147483647).optional(),
    dir: z.enum(['asc', 'desc']).default('desc'),
    /**
     * Die Seite ausliefern, auf der *dieses* Bild steht – statt der über `page`
     * angeforderten.
     *
     * Für die Bildseite, die ohne Listenzusammenhang geöffnet wurde: sie braucht
     * die Nachbarn eines Bildes, kennt aber seine Stelle in der Liste nicht.
     * Ausrechnen kann sie nur die Datenbank; von außen ginge es nur durch
     * Seitenweises Suchen. Die tatsächlich gelieferte Seite steht in der
     * Antwort.
     */
    focus: uuid.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ── Bildmarkierungen ───────────────────────────────────────────────────────
/** Normalisierte Koordinate: 0 = linker/oberer Rand, 1 = rechter/unterer. */
const unitInterval = z.coerce.number().min(0).max(1);
export const mediaRegionInputSchema = z
    .object({
    x: unitInterval,
    y: unitInterval,
    width: z.coerce.number().min(0.005, 'Markierung ist zu klein').max(1),
    height: z.coerce.number().min(0.005, 'Markierung ist zu klein').max(1),
    personId: optionalUuid.default(null),
    label: shortText.nullable().default(null),
    /** Wie alt die Person darauf aussieht – siehe `APPARENT_AGES`. */
    apparentAge: z.enum(APPARENT_AGES).nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.x + v.width > 1.001) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Markierung reicht über den rechten Bildrand hinaus',
            path: ['width'],
        });
    }
    if (v.y + v.height > 1.001) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Markierung reicht über den unteren Bildrand hinaus',
            path: ['height'],
        });
    }
});
export const mediaRegionPatchSchema = mediaRegionInputSchema.innerType().partial();
/**
 * Ein Gesicht, das die automatische Erkennung gefunden hat.
 *
 * Ohne `personId` und ohne `label` – der erste Schritt setzt nur den Rahmen.
 * Wer darin zu sehen ist, ordnet danach ein Mensch zu; das Modell hinter dem
 * Knopf kann Gesichter finden, aber keine wiedererkennen.
 */
export const detectedFaceSchema = z
    .object({
    x: unitInterval,
    y: unitInterval,
    width: z.coerce.number().min(0.005, 'Markierung ist zu klein').max(1),
    height: z.coerce.number().min(0.005, 'Markierung ist zu klein').max(1),
    /** Sicherheit des Modells, 0…1. Wird mitgeschrieben, nicht angezeigt. */
    confidence: z.coerce.number().min(0).max(1).nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.x + v.width > 1.001) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Markierung reicht über den rechten Bildrand hinaus',
            path: ['width'],
        });
    }
    if (v.y + v.height > 1.001) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Markierung reicht über den unteren Bildrand hinaus',
            path: ['height'],
        });
    }
});
/**
 * Ein ganzer Erkennungslauf in einer Anfrage.
 *
 * Absichtlich als Stapel und nicht als n einzelne Anfragen: bei zwölf
 * Gesichtern wären das zwölf Runden über das Netz, zwölf Protokolleinträge und
 * ein halb angelegter Zustand, wenn die achte scheitert.
 */
export const detectedRegionsInputSchema = z.object({
    regions: z
        .array(detectedFaceSchema)
        .min(1, 'Keine Gesichter übergeben')
        .max(MAX_DETECTED_FACES, `Höchstens ${MAX_DETECTED_FACES} Gesichter pro Durchlauf`),
});
// ── Quellen ────────────────────────────────────────────────────────────────
export const sourceInputSchema = z.object({
    title: shortText.min(1, 'Titel fehlt'),
    kind: z.enum(SOURCE_KINDS).default('document'),
    citation: longText.nullable().default(null),
    url: z.union([z.string().trim().url(), z.literal('')]).transform((v) => (v === '' ? null : v)).nullable().default(null),
    notes: longText.nullable().default(null),
    mediaId: optionalUuid.default(null),
});
export const sourceLinkSchema = z.object({
    entityType: z.enum(CITABLE_ENTITIES),
    entityId: uuid,
    page: shortText.nullable().default(null),
});
export const sourceListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    kind: z.enum(SOURCE_KINDS).optional(),
    sort: z.enum(['title', 'kind', 'createdAt']).default('title'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
// ── Graph ──────────────────────────────────────────────────────────────────
export const treeQuerySchema = z.object({
    /** Ohne Wurzel wird der gesamte Bestand als ein Graph gezeichnet. */
    rootId: uuid.optional(),
    /**
     * Ohne Angabe laufen die beiden Wege bis zum Ende – so weit, wie erfasst
     * ist. Vorerst schickt die Oberfläche hier nichts: eine gewählte Wurzel
     * zeigt ihre ganze Linie, nach oben und nach unten. Die Begrenzung bleibt
     * als Möglichkeit stehen, weil der Ausschnitt um eine Person
     * wiederkommen wird.
     */
    ancestorDepth: z.coerce.number().int().min(0).max(8).optional(),
    descendantDepth: z.coerce.number().int().min(0).max(8).optional(),
    includePartners: queryBoolean.default(true),
    /**
     * Personen, die im Ausschnitt stehen *müssen* – Kennungen, durch Komma
     * getrennt.
     *
     * Gedacht für den hervorgehobenen Verwandtschaftsweg. Eine gewählte Wurzel
     * zeigt ihre Vorfahren und ihre Nachkommen, sonst niemanden; ein Weg zu
     * einer Cousine läuft aber über Geschwister, Onkel und Angeheiratete, also
     * genau durch das, was dort fehlt. Statt dass die goldene Kette im Bild
     * abreißt, hängt der Server diese Zweige an der Stelle ein, an die sie
     * gehören.
     */
    includeIds: z
        .union([z.string(), z.array(uuid)])
        .optional()
        .transform((value) => (typeof value === 'string' ? value.split(',') : (value ?? [])).filter(Boolean))
        .pipe(z.array(uuid).max(40)),
});
export const relationPathQuerySchema = z.object({
    fromId: uuid,
    toId: uuid,
});
// ── Familienfragen ─────────────────────────────────────────────────────────
export const questionInputSchema = z
    .object({
    kind: z.enum(QUESTION_KINDS),
    prompt: z.string().trim().min(3, 'Die Frage ist zu kurz').max(2000),
    /** Bei „Fehlende Angabe“: welches Feld gesucht ist. */
    field: z.enum(PROPOSABLE_FIELDS).nullable().default(null),
    subjectPersonId: uuid.nullable().default(null),
    subjectMediaId: uuid.nullable().default(null),
    subjectRegionId: uuid.nullable().default(null),
    subjectRelationshipId: uuid.nullable().default(null),
    /** Leer = an alle anderen Mitglieder. */
    recipientIds: z.array(uuid).max(200).default([]),
})
    .superRefine((value, ctx) => {
    if (!value.subjectPersonId &&
        !value.subjectMediaId &&
        !value.subjectRegionId &&
        !value.subjectRelationshipId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Frage braucht einen Gegenstand: eine Person, ein Bild oder eine Markierung',
            path: ['subjectPersonId'],
        });
    }
    if (value.kind === 'missing_fact' && !value.field) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Bitte angeben, welche Angabe fehlt',
            path: ['field'],
        });
    }
    if (value.kind === 'missing_fact' && !value.subjectPersonId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine fehlende Angabe gehört zu einer Person',
            path: ['subjectPersonId'],
        });
    }
    // Eine Verbindungsfrage hängt entweder an einer bestehenden Verbindung
    // („waren die verheiratet?") oder an einer Person („wer war ihr Vater?").
    if (value.kind === 'relationship' && !value.subjectRelationshipId && !value.subjectPersonId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Frage zur Verbindung braucht eine Person oder eine bestehende Verbindung',
            path: ['subjectPersonId'],
        });
    }
});
/**
 * Eine Antwort. Der Unterschied zu einer Nachricht: `proposal` trägt einen
 * konkreten Wert, der nach Freigabe im Archiv landet.
 */
export const answerInputSchema = z
    .object({
    body: z.string().trim().max(4000).nullable().default(null),
    proposalKind: z.enum(PROPOSAL_KINDS).default('none'),
    proposedField: z.enum(PROPOSABLE_FIELDS).nullable().default(null),
    proposedPersonId: uuid.nullable().default(null),
    proposedDate: fuzzyDateSchema.nullable().default(null),
    proposedPlaceId: uuid.nullable().default(null),
    proposedText: shortText.nullable().default(null),
    /** Nur bei `proposalKind: 'relationship'`. */
    proposedRelationRole: z.enum(RELATION_ROLES).nullable().default(null),
    proposedSubtype: z.enum(RELATIONSHIP_SUBTYPES).nullable().default(null),
    proposedEndDate: fuzzyDateSchema.nullable().default(null),
})
    .superRefine((value, ctx) => {
    if (value.proposalKind === 'none' && !value.body) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Bitte etwas schreiben oder einen Vorschlag machen',
            path: ['body'],
        });
    }
    if (value.proposalKind === 'person' && !value.proposedPersonId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Bitte die Person auswählen',
            path: ['proposedPersonId'],
        });
    }
    if (value.proposalKind === 'relationship') {
        // Für eine *neue* Verbindung braucht es Rolle und Gegenperson; für die
        // Korrektur einer bestehenden reicht irgendeine Angabe.
        const hasNew = Boolean(value.proposedRelationRole && value.proposedPersonId);
        const hasChange = Boolean(value.proposedSubtype || value.proposedDate || value.proposedEndDate || value.proposedPlaceId);
        if (!hasNew && !hasChange) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Bitte die Person und ihre Rolle angeben – oder etwas an der Verbindung ändern',
                path: ['proposedPersonId'],
            });
        }
        if (value.proposedRelationRole && !value.proposedPersonId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Bitte die Person auswählen',
                path: ['proposedPersonId'],
            });
        }
    }
    if (value.proposalKind === 'fact') {
        if (!value.proposedField) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Bitte das Feld angeben',
                path: ['proposedField'],
            });
            return;
        }
        const type = PROPOSABLE_FIELD_TYPES[value.proposedField];
        const filled = type === 'date' ? Boolean(value.proposedDate) :
            type === 'place' ? Boolean(value.proposedPlaceId) :
                Boolean(value.proposedText);
        if (!filled) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Bitte einen Wert für den Vorschlag angeben',
                path: ['proposedText'],
            });
        }
    }
});
export const answerDecisionSchema = z.object({
    note: z.string().trim().max(1000).nullable().default(null),
});
export const questionListQuerySchema = z.object({
    /** inbox = an mich gerichtet, review = Vorschläge zur Freigabe, mine = von mir gestellt. */
    box: z.enum(['inbox', 'review', 'mine', 'all']).default('inbox'),
    status: z.enum(QUESTION_STATUSES).optional(),
    personId: uuid.optional(),
    mediaId: uuid.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
// ── Lücken / Aufgaben ──────────────────────────────────────────────────────
export const gapListQuerySchema = z.object({
    kind: z.enum(GAP_KINDS).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ── Aktivitäten ────────────────────────────────────────────────────────────
export const activityListQuerySchema = z.object({
    /**
     * Wessen Aktivitäten. Nur Administratoren dürfen hier jemand anderen
     * einsetzen – erzwungen wird das im Endpunkt, nicht im Schema, damit die
     * Regel neben der Rolle steht und nicht neben der Validierung.
     */
    userId: uuid.optional(),
    action: z.enum(AUDIT_ACTIONS).optional(),
    entityType: z.enum(AUDIT_ENTITY_TYPES).optional(),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
// ── Suche ──────────────────────────────────────────────────────────────────
export const searchQuerySchema = z.object({
    q: z.string().trim().min(1).max(200),
    limit: z.coerce.number().int().min(1).max(50).default(8),
});
//# sourceMappingURL=schemas.js.map